import type { Incident } from '../../services/incidentsApi';
import { representativeHasCloudClip } from './groupedEvidenceAutoPromote';

/** After this age, a still-missing clip is treated as unavailable — not "still uploading". */
export const STALE_CLIP_PENDING_MS = 60 * 60 * 1000;

export type HonestClipStatus = 'AVAILABLE' | 'PENDING' | 'FAILED' | 'MISSING';

function incidentAgeMs(incident: Incident | null | undefined, nowMs: number): number | null {
  const raw =
    (incident as any)?.created_at
    || (incident as any)?.event_started_at
    || (incident as any)?.started_at;
  const ms = Date.parse(String(raw || '').trim());
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, nowMs - ms);
}

/**
 * Buyer-facing clip presence.
 * PENDING only while a recent upload is still plausible; otherwise MISSING.
 */
export function resolveHonestClipStatus(
  incident: Incident | null | undefined,
  options?: { nowMs?: number; hasClipArtifact?: boolean; staleAfterMs?: number },
): HonestClipStatus {
  if (!incident) return 'MISSING';
  const raw = String((incident as any)?.clip_status || '').trim().toUpperCase();
  if (
    raw === 'AVAILABLE'
    || raw === 'READY'
    || Boolean(options?.hasClipArtifact)
    || representativeHasCloudClip(incident)
  ) {
    return 'AVAILABLE';
  }
  if (raw === 'FAILED') return 'FAILED';

  const ageMs = incidentAgeMs(incident, options?.nowMs ?? Date.now());
  const staleAfter = options?.staleAfterMs ?? STALE_CLIP_PENDING_MS;
  if (ageMs != null && ageMs >= staleAfter) {
    return 'MISSING';
  }
  // Explicit edge-not-available after promote attempts.
  if ((incident as any)?.clip_available_on_edge === false && ageMs != null && ageMs >= 5 * 60 * 1000) {
    return 'MISSING';
  }
  return 'PENDING';
}

export function honestClipStatusLabel(status: HonestClipStatus): string {
  switch (status) {
    case 'AVAILABLE':
      return 'Clip available';
    case 'FAILED':
      return 'Clip failed';
    case 'MISSING':
      return 'No video';
    default:
      return 'Clip pending';
  }
}

export function honestClipMissingHelper(): string {
  return (
    'No video was captured or retained for this occurrence. '
    + 'Review the snapshot, or open another occurrence that has video.'
  );
}
